import { addDays, addMonths, endOfMonth, endOfWeek, endOfYear, startOfMonth, startOfWeek, startOfYear } from "date-fns";
import { uiToServer } from "../../../app/utils/utilityFunctions";
import { useSelectDashboardDateRangeOrder } from "../../../hooks/valueProviderHooks";

// Fixed task-type -> series color/label map for the customer trends stacked bar chart.
// Order and color assignment are fixed (not derived from data) so a type's color/position
// never changes when the period filter changes the set/ranking of types actually present.
const TASK_TYPE_SERIES = [
  { id: "INS", label: "Installation", light: "#2a78d6", dark: "#3987e5" },
  { id: "QMP", label: "QMP", light: "#1baf7a", dark: "#199e70" },
  { id: "WTDR", label: "Withdrawal", light: "#eda100", dark: "#c98500" },
  { id: "PS", label: "PM/Sanitation", light: "#008300", dark: "#008300" },
  { id: "PRJ", label: "Project", light: "#4a3aa7", dark: "#9085e9" },
  { id: "BRKDN", label: "Breakdown", light: "#e34948", dark: "#e66767" },
  { id: "DLVR", label: "Delivery", light: "#e87ba4", dark: "#d55181" },
  { id: "SR", label: "Site Reece", light: "#eb6834", dark: "#d95926" },
  { id: "SPN_BRKDN", label: "Span Breakdown", light: "#913f82", dark: "#ad3d9a" },
  { id: "SPN_PRJ", label: "Span Project", light: "#ab2e45", dark: "#d30046" },
  { id: "SP_PRT_OD", label: "Spare Part Order", light: "#7960fb", dark: "#6c50e9" },
  { id: "WRHS_NIKGIL", label: "Warehouse NIKGIL", light: "#00b4ff", dark: "#00719e" },
];

// Any task type outside the fixed slots above (e.g. VRP, OTHR) is folded into this bucket
// rather than generating a new hue, per the "never cycle colors" rule.
const OTHER_TYPES_SERIES = { id: "OTHER", label: "Other Types", light: "#898781", dark: "#898781" };

export const getTaskTypeSeriesConfig = (typeId) => TASK_TYPE_SERIES.find((t) => t.id === typeId) ?? OTHER_TYPES_SERIES;

// Builds ApexCharts-ready stacked series from the API's { series: [{ type, data }], categories } shape.
// Types without a fixed slot are summed together into the "Other Types" series.
export const buildStackedSeries = (rawSeries = [], categories = [], mode = "light") => {
  const colorKey = mode === "dark" ? "dark" : "light";
  const otherData = Array(categories.length).fill(0);
  let hasOther = false;

  const known = TASK_TYPE_SERIES.map((cfg) => {
    const match = rawSeries.find((s) => s.type === cfg.id);
    return {
      name: cfg.label,
      color: cfg[colorKey],
      data: match ? match.data : Array(categories.length).fill(0),
    };
  }).filter((series) => series.data.some((v) => v > 0));

  rawSeries
    .filter((s) => !TASK_TYPE_SERIES.some((cfg) => cfg.id === s.type))
    .forEach((s) => {
      hasOther = true;
      s.data.forEach((v, idx) => {
        otherData[idx] += v;
      });
    });

  if (hasOther) {
    known.push({ name: OTHER_TYPES_SERIES.label, color: OTHER_TYPES_SERIES[colorKey], data: otherData });
  }

  return known;
};

// Same preset list/logic as the Tasks page's date range filter (see
// dateOptions in src/components/config/generalTableConfig.js), so the
// Dashboard filter behaves identically.
const DASHBOARD_DATE_RANGE_OPTIONS = [
  {
    value: 0,
    label: "This Week",
    returnDataValue: {
      value: 0,
      from: uiToServer(startOfWeek(new Date())),
      to: uiToServer(endOfWeek(new Date())),
    },
  },
  {
    value: 1,
    label: "This Month",
    returnDataValue: {
      value: 1,
      from: uiToServer(startOfMonth(new Date())),
      to: uiToServer(endOfMonth(addDays(new Date(), 1))),
    },
  },
  {
    value: 2,
    label: "Last Month",
    returnDataValue: {
      value: 2,
      from: uiToServer(startOfMonth(addMonths(new Date(), -1))),
      to: uiToServer(endOfMonth(addMonths(new Date(), -1))),
    },
  },
  {
    value: 3,
    label: "This Year",
    returnDataValue: {
      value: 3,
      from: uiToServer(startOfYear(new Date())),
      to: uiToServer(endOfYear(new Date())),
    },
  },
  {
    value: 4,
    label: "All Time",
    returnDataValue: {
      value: 4,
      from: null,
      to: null,
    },
  },
];

// Page-level time range filter for the Dashboard, reusing the same
// SelectOptionsFieldWithCustomConfig component and Custom Date dialog
// the Tasks page uses.
export const dashboardDateRangeSelectConfig = {
  display: true,
  id: "select-dashboard-date-range",
  inputLabel: "Time Range",
  size: "small",
  defaultValue: 1,
  defaultDataValue: DASHBOARD_DATE_RANGE_OPTIONS[1].returnDataValue,
  options: DASHBOARD_DATE_RANGE_OPTIONS,
  useValueProvider: useSelectDashboardDateRangeOrder,
  customOption: {
    label: "Custom Date",
    value: "custom_date",
  },
  sx: { minWidth: "170px" },
};
